'use client';
import React from 'react';
import { Calendar, Clock, MapPin, Download, Star } from 'lucide-react';

const pastBookings = [
  {
    id: 'YGN-2026-P001',
    ritual: 'Navgraha Shanti Puja',
    priest: 'Pandit Arun Joshi',
    date: 'Jul 20, 2026',
    time: '8:00 AM',
    address: '12, Lotus Lane, Koramangala, Bengaluru',
    price: '₹4,500',
    duration: '4 hours',
    rating: 5,
    emoji: '🌟',
  },
  {
    id: 'YGN-2026-P002',
    ritual: 'Lakshmi Puja',
    priest: 'Pandit Ramesh Kumar',
    date: 'Jun 15, 2026',
    time: '7:00 AM',
    address: '45, Tulsi Nagar, Indiranagar, Bengaluru',
    price: '₹2,800',
    duration: '2.5 hours',
    rating: 4,
    emoji: '🪷',
  },
  {
    id: 'YGN-2026-P003',
    ritual: 'Griha Pravesh',
    priest: 'Pandit Suresh Sharma',
    date: 'May 3, 2026',
    time: '10:00 AM',
    address: '78, Green Park, Whitefield, Bengaluru',
    price: '₹6,000',
    duration: '5 hours',
    rating: 5,
    emoji: '🏠',
  },
  {
    id: 'YGN-2026-P004',
    ritual: 'Satyanarayan Puja',
    priest: 'Pandit Arun Joshi',
    date: 'Mar 28, 2026',
    time: '9:00 AM',
    address: '12, Lotus Lane, Koramangala, Bengaluru',
    price: '₹3,200',
    duration: '3 hours',
    rating: 5,
    emoji: '🪔',
  },
];

export default function PastRituals() {
  const handleDownload = (booking: typeof pastBookings[0]) => {
    const content = `BOOKING CONFIRMATION\n\nBooking ID: ${booking.id}\nRitual: ${booking.ritual}\nPriest: ${booking.priest}\nDate: ${booking.date}\nTime: ${booking.time}\nAddress: ${booking.address}\nDuration: ${booking.duration}\nAmount: ${booking.price}\nStatus: COMPLETED\n\nThank you for booking with Yagnick.\nFor support: support@yagnick.com`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Booking-${booking.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="font-display text-xl font-bold text-maroon">Past Rituals</h2>
        <p className="text-sm text-maroon/50 mt-0.5">{pastBookings.length} completed rituals</p>
      </div>

      <div className="space-y-4">
        {pastBookings.map((booking) => (
          <div key={booking.id} className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gold/10 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-cream flex items-center justify-center text-xl flex-shrink-0">
                {booking.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-maroon">{booking.ritual}</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">✓ Completed</span>
                </div>
                <p className="text-xs text-maroon/50 mt-0.5">ID: {booking.id}</p>
              </div>
            </div>

            {/* Details */}
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-2.5">
                <Calendar size={14} className="text-maroon/30 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-maroon/40 font-medium">Date & Time</p>
                  <p className="text-sm text-maroon font-medium">{booking.date} · {booking.time}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Clock size={14} className="text-maroon/30 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-maroon/40 font-medium">Duration</p>
                  <p className="text-sm text-maroon font-medium">{booking.duration}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 sm:col-span-2">
                <MapPin size={14} className="text-maroon/30 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-maroon/40 font-medium">Location</p>
                  <p className="text-sm text-maroon font-medium">{booking.address}</p>
                </div>
              </div>
            </div>

            {/* Priest + Rating + Price */}
            <div className="px-5 py-3 bg-cream/40 border-t border-gold/10 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs text-maroon/40">Priest</p>
                <p className="text-sm font-semibold text-maroon">{booking.priest}</p>
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={13} className={i < booking.rating ? 'text-gold fill-gold' : 'text-maroon/20'} />
                ))}
              </div>
              <div className="text-right">
                <p className="text-xs text-maroon/40">Amount Paid</p>
                <p className="text-sm font-bold text-maroon">{booking.price}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-5 py-3 border-t border-gold/10 flex flex-wrap gap-2">
              <button
                onClick={() => handleDownload(booking)}
                className="flex items-center gap-1.5 text-xs font-semibold text-maroon/60 border border-gold/20 px-3 py-1.5 rounded-full hover:bg-cream transition-colors"
              >
                <Download size={12} /> Download Receipt
              </button>
              <a
                href="/booking-flow"
                className="flex items-center gap-1.5 text-xs font-semibold text-saffron border border-saffron/30 px-3 py-1.5 rounded-full hover:bg-saffron/5 transition-colors"
              >
                Book Again
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
